import { useServices } from '@/app/providers';
import { AccessLogsPage } from '@/modules/guard';
import { AccessLogList } from '@/presentation/components/organisms/AccessLogList';
import { useI18n } from '@/shared/i18n';
import type { AccessLog } from '@/shared/types';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

// RESIDENT/FAMILIAR tienen `AccessLog:read` propio (condicionado a su userId, para ver
// su propio historial), pero ese permiso se otorga por tipo de sujeto sin evaluar la
// condición — por eso pasaban el chequeo de ruta de /registro-accesos y caían en la vista
// de guardia (que pega a v1/access-control/logs y v1/guard/shift/current, endpoints solo
// para ADMIN/GUARD). Acá se les muestra su propio historial (v1/access-control/logs/me,
// igual que la pestaña "LOGS" de /accesos) en vez de redirigirlos u ocultarles la pantalla.
const SELF_SERVICE_TENANT_ROLES = new Set(['RESIDENT', 'FAMILIAR']);

const MyAccessLogs = () => {
  const { t } = useI18n();
  const { accessLogRepo, currentUser } = useServices();
  const [logs, setLogs] = useState<AccessLog[]>([]);
  const [loading, setLoading] = useState(true);
  const lastRefreshRef = useRef<number>(0);

  const refreshLogs = useCallback(async () => {
    if (!currentUser) return;
    const now = Date.now();
    if (now - lastRefreshRef.current < 3000) return;
    lastRefreshRef.current = now;
    try {
      const data = await accessLogRepo.getLogs({ userId: currentUser.id });
      setLogs(data);
    } catch {
      // silent — no bloquear la pantalla por un error de fondo
    }
  }, [currentUser, accessLogRepo]);

  useEffect(() => {
    if (!currentUser) return;
    setLoading(true);
    refreshLogs().finally(() => setLoading(false));
  }, [currentUser, refreshLogs]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-800">{t('accessLogs.title')}</h2>
        <p className="text-slate-500 text-sm">{t('accessLogs.subtitle')}</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      ) : (
        <AccessLogList logs={logs} onRequestRefresh={refreshLogs} />
      )}
    </div>
  );
};

export const AccessLogs = () => {
  const { currentUser } = useServices();
  const tenantRole = String(currentUser?.tenantRole || '').toUpperCase();

  if (SELF_SERVICE_TENANT_ROLES.has(tenantRole)) {
    return <MyAccessLogs />;
  }

  return <AccessLogsPage />;
};
